import React, { useMemo, useState } from "react"

import { ClientOnly } from "../common"
import DynamicMap3D from "../idealline/DynamicMap3D"

const nearestPointIndex = (points, distanceM) =>
    points.reduce(
        (best, point, index) => {
            const difference = Math.abs(point.distanceM - distanceM)
            return difference < best.difference ? { difference, index } : best
        },
        { difference: Infinity, index: 0 }
    ).index

const MapUnavailable = () => (
    <div className="flex h-full min-h-[420px] items-center justify-center rounded-2xl bg-slate-950 p-8 text-center text-sm font-semibold text-white">
        The interactive Google racing line map is unavailable in this preview.
    </div>
)

const CoachingRacingLineMap = ({
    points,
    sections,
    selectedSection,
    onSectionSelect,
}) => {
    const [mapFailed, setMapFailed] = useState(false)
    const mapKey = process.env.GATSBY_GMAPS_API_KEY
    const mapPoints = useMemo(
        () =>
            points.map((point) => ({
                lat: point.latitude,
                lng: point.longitude,
                altitude: point.altitude ?? null,
                distanceM: point.distanceM,
                type:
                    point.brakePct > 5
                        ? "brake"
                        : point.throttlePct > 75
                        ? "acc"
                        : "coast",
            })),
        [points]
    )
    const mapCenter = useMemo(() => {
        const latitudes = mapPoints.map((point) => point.lat)
        const longitudes = mapPoints.map((point) => point.lng)
        return {
            lat: (Math.min(...latitudes) + Math.max(...latitudes)) / 2,
            lng: (Math.min(...longitudes) + Math.max(...longitudes)) / 2,
        }
    }, [mapPoints])
    const selectedPointIndex = useMemo(
        () =>
            nearestPointIndex(
                mapPoints,
                selectedSection.focusDistanceM ??
                    (selectedSection.startM + selectedSection.endM) / 2
            ),
        [mapPoints, selectedSection]
    )

    const handlePointSelect = (pointIndex) => {
        const point = mapPoints[pointIndex]
        if (!point) return
        const section = sections.find(
            (candidate) =>
                point.distanceM >= candidate.startM &&
                point.distanceM <= candidate.endM
        )
        if (section) onSectionSelect(section.id)
    }

    const fallback = <MapUnavailable />
    if (!mapKey || mapFailed) return fallback

    return (
        <div className="relative h-full min-h-[420px] overflow-hidden rounded-2xl bg-slate-950">
            <ClientOnly fallback={fallback}>
                <DynamicMap3D
                    addPoint={() => {}}
                    apikey={mapKey}
                    embedLayout
                    fillContainer
                    lat={mapCenter.lat}
                    lng={mapCenter.lng}
                    onLoadError={() => setMapFailed(true)}
                    onPointSelect={handlePointSelect}
                    pointType="coast"
                    points={mapPoints}
                    readOnly
                    selectedPointIndex={selectedPointIndex}
                    selectedRange={{
                        startM: selectedSection.startM,
                        endM: selectedSection.endM,
                    }}
                    showControls
                    zoom={17}
                />
            </ClientOnly>
            <div className="pointer-events-none absolute bottom-4 left-4 rounded-full bg-black/70 px-3 py-1 text-xs font-semibold text-white">
                Brake red · coast yellow · throttle green
            </div>
        </div>
    )
}

export default CoachingRacingLineMap
